/**
 * Pure-code 88-key strip (subset by range) drawn on HTML5 Canvas.
 * Supports Phase 5 notation labels + practice guide / wrong flashes.
 */

import {
  midiToNoteName,
  labelForMidi,
  defaultCPositionFinger,
  type NotationMode,
} from "@piano-tutor/shared";

export interface PianoCanvasOptions {
  lowMidi?: number;
  highMidi?: number;
}

const WHITE_IN_OCTAVE = [0, 2, 4, 5, 7, 9, 11];

/** Expected / guide — dark green so students clearly see the target. */
const GUIDE_WHITE = "#1b6b4a";
const GUIDE_WHITE_EDGE = "#0f4530";
const GUIDE_BLACK = "#2a9a6a";
/** Wrong press flash */
const WRONG_WHITE = "#c62828";
const WRONG_BLACK = "#8e1b1b";
/** Correct hit flash */
const HIT_WHITE = "#43a047";
const HIT_BLACK = "#2e7d32";

function isWhite(midi: number): boolean {
  return WHITE_IN_OCTAVE.includes(midi % 12);
}

export class PianoCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private low: number;
  private high: number;
  private active = new Set<number>();
  private whiteKeys: number[] = [];
  private blackKeys: number[] = [];
  private dpr = 1;
  private notationMode: NotationMode = "letter";
  private guideNotes = new Set<number>();
  private wrongNotes = new Set<number>();
  private hitNotes = new Set<number>();
  private computerKeyLabels: ReadonlyMap<number, string> | null = null;
  /** Lesson finger numbers for currently expected keys (teacher overlay). */
  private lessonFingers: ReadonlyMap<number, number> | null = null;

  onPointerNote: ((type: "noteon" | "noteoff", midi: number) => void) | null = null;

  constructor(canvas: HTMLCanvasElement, options: PianoCanvasOptions = {}) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    this.ctx = ctx;
    this.low = options.lowMidi ?? 48;
    this.high = options.highMidi ?? 84;
    this.rebuildKeyLists();
    this.resize();
    this.bindPointer();
    window.addEventListener("resize", () => this.resize());
  }

  setNotationMode(mode: NotationMode): void {
    this.notationMode = mode;
    this.draw();
  }

  /** Laptop mode overrides musical notation with physical QWERTY key labels. */
  setComputerKeyLabels(labels: ReadonlyMap<number, string> | null): void {
    this.computerKeyLabels = labels;
    this.draw();
  }

  /** Teacher finger numbers for expected lesson keys (shown on those keys). */
  setLessonFingers(fingers: ReadonlyMap<number, number> | null): void {
    this.lessonFingers = fingers && fingers.size ? fingers : null;
    this.draw();
  }

  setGuideNotes(notes: number[]): void {
    this.guideNotes = new Set(notes);
    this.draw();
  }

  setActiveNotes(notes: number[]): void {
    this.active = new Set(notes);
    this.draw();
  }

  /** Practice grading flashes — wrong=red, hit=bright green. */
  setKeyFeedback(wrong: number[], hit: number[]): void {
    this.wrongNotes = new Set(wrong);
    this.hitNotes = new Set(hit);
    this.draw();
  }

  get lowMidi(): number {
    return this.low;
  }

  get highMidi(): number {
    return this.high;
  }

  get keyCount(): number {
    return this.high - this.low + 1;
  }

  /** Change visible keyboard range (e.g. 49 / 61 / 88-key layouts). */
  setRange(lowMidi: number, highMidi: number): void {
    this.low = Math.max(0, Math.min(127, Math.round(lowMidi)));
    this.high = Math.max(this.low, Math.min(127, Math.round(highMidi)));
    this.rebuildKeyLists();
    this.resize();
  }

  /** Normalized (0–1) position of a MIDI key center for FX anchoring. */
  keyNormPos(midi: number): { nx: number; ny: number } {
    const cssW = this.canvas.width / this.dpr || 1;
    const cssH = this.canvas.height / this.dpr || 1;
    const { w } = this.whiteGeometry(cssW, cssH);
    if (isWhite(midi)) {
      const i = this.whiteKeys.indexOf(midi);
      const idx = i >= 0 ? i : this.whiteIndexForMidi(midi);
      return { nx: ((idx + 0.5) * w) / cssW, ny: 0.55 };
    }
    const wi = this.whiteIndexForMidi(midi);
    return { nx: ((wi + 0.85) * w) / cssW, ny: 0.28 };
  }

  private rebuildKeyLists(): void {
    this.whiteKeys = [];
    this.blackKeys = [];
    for (let n = this.low; n <= this.high; n++) {
      if (isWhite(n)) this.whiteKeys.push(n);
      else this.blackKeys.push(n);
    }
  }

  resize(): void {
    const parent = this.canvas.parentElement;
    const cssW = parent?.clientWidth || 800;
    // Fewer keys → taller keys so a 49-key board looks physical, not tiny.
    const whiteN = Math.max(1, this.whiteKeys.length);
    const heightFactor = Math.min(0.34, Math.max(0.2, 7.2 / whiteN));
    // 7–10″ tablets: slightly taller keys for finger targets; short landscape stays compact.
    const shortLandscape =
      typeof window !== "undefined" &&
      window.matchMedia("(max-height: 560px) and (orientation: landscape)").matches;
    const tablet =
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 721px) and (max-width: 1180px)").matches;
    const maxH = shortLandscape ? 160 : tablet ? 300 : 280;
    const minH = shortLandscape ? 120 : tablet ? 170 : 150;
    const cssH = Math.min(maxH, Math.max(minH, cssW * heightFactor));
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(cssW * this.dpr);
    this.canvas.height = Math.floor(cssH * this.dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.draw();
  }

  private whiteGeometry(cssW: number, cssH: number) {
    const count = this.whiteKeys.length;
    const w = cssW / count;
    return { count, w, h: cssH };
  }

  private midiAtWhiteIndex(index: number): number {
    return this.whiteKeys[index];
  }

  private whiteIndexForMidi(midi: number): number {
    let idx = -1;
    for (let i = 0; i < this.whiteKeys.length; i++) {
      if (this.whiteKeys[i] <= midi) idx = i;
      else break;
    }
    return idx;
  }

  private label(midi: number): string {
    const lessonFinger = this.lessonFingers?.get(midi);
    if (lessonFinger != null && this.guideNotes.has(midi)) {
      return String(lessonFinger);
    }
    const computerLabel =
      this.notationMode === "letter" ? this.computerKeyLabels?.get(midi) : undefined;
    if (computerLabel) return computerLabel;
    if (this.notationMode === "staff") {
      const { name, octave } = midiToNoteName(midi);
      return `${name}${octave}`;
    }
    const finger =
      this.lessonFingers?.get(midi) ?? defaultCPositionFinger(midi);
    return labelForMidi(midi, this.notationMode, {
      finger: finger as 1 | 2 | 3 | 4 | 5 | undefined,
      withOctave: this.notationMode === "letter",
    });
  }

  private whiteFill(midi: number): { fill: string; label: string; edge?: string } {
    if (this.wrongNotes.has(midi)) return { fill: WRONG_WHITE, label: "#fff" };
    if (this.hitNotes.has(midi)) return { fill: HIT_WHITE, label: "#fff" };
    if (this.guideNotes.has(midi))
      return { fill: GUIDE_WHITE, label: "#f5fff8", edge: GUIDE_WHITE_EDGE };
    if (this.active.has(midi)) return { fill: "#c5e0d6", label: "rgba(26,22,18,0.75)" };
    return { fill: "#f7f2ea", label: "rgba(26,22,18,0.45)" };
  }

  draw(): void {
    const cssW = this.canvas.width / this.dpr;
    const cssH = this.canvas.height / this.dpr;
    const { w, h } = this.whiteGeometry(cssW, cssH);
    const ctx = this.ctx;

    ctx.clearRect(0, 0, cssW, cssH);

    // Case / wood shelf under keys for a real-piano silhouette
    const shelf = ctx.createLinearGradient(0, h * 0.92, 0, cssH);
    shelf.addColorStop(0, "#2a1c12");
    shelf.addColorStop(1, "#120c08");
    ctx.fillStyle = shelf;
    ctx.fillRect(0, h * 0.92, cssW, cssH - h * 0.92);

    // Soft ambient shadow behind the whole bank
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.fillRect(0, 2, cssW, 6);

    this.whiteKeys.forEach((midi, i) => {
      const x = i * w;
      const style = this.whiteFill(midi);
      const on = this.active.has(midi);
      const guide = this.guideNotes.has(midi);
      const wrong = this.wrongNotes.has(midi);
      const hit = this.hitNotes.has(midi);
      const pressed = on || hit;

      // 3D white key body — vertical ivory gradient + front bevel
      const bodyH = pressed ? h * 0.97 : h;
      const y0 = pressed ? 3 : 0;
      const g = ctx.createLinearGradient(x, y0, x, y0 + bodyH);
      if (wrong) {
        g.addColorStop(0, "#e57373");
        g.addColorStop(0.55, WRONG_WHITE);
        g.addColorStop(1, "#8e1b1b");
      } else if (hit) {
        g.addColorStop(0, "#81c784");
        g.addColorStop(0.55, HIT_WHITE);
        g.addColorStop(1, "#2e7d32");
      } else if (guide) {
        g.addColorStop(0, "#3d9a72");
        g.addColorStop(0.5, GUIDE_WHITE);
        g.addColorStop(1, GUIDE_WHITE_EDGE);
      } else if (on) {
        g.addColorStop(0, "#e8f5f0");
        g.addColorStop(0.6, "#c5e0d6");
        g.addColorStop(1, "#8fb5a6");
      } else {
        g.addColorStop(0, "#fffcf7");
        g.addColorStop(0.45, "#f3ebe0");
        g.addColorStop(1, "#d9cfc2");
      }
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(x + 1, y0);
      ctx.lineTo(x + w - 1, y0);
      ctx.lineTo(x + w - 2, y0 + bodyH - 4);
      ctx.quadraticCurveTo(x + w / 2, y0 + bodyH, x + 2, y0 + bodyH - 4);
      ctx.closePath();
      ctx.fill();

      // Side edge (depth)
      ctx.fillStyle = "rgba(40,30,20,0.18)";
      ctx.fillRect(x + w - 3, y0 + 2, 2, bodyH - 10);

      // Top lip highlight
      ctx.fillStyle = "rgba(255,255,255,0.45)";
      ctx.fillRect(x + 2, y0 + 1, w - 5, 3);

      ctx.strokeStyle = style.edge ?? "rgba(26,22,18,0.28)";
      ctx.lineWidth = guide || wrong || hit ? 2 : 1;
      ctx.strokeRect(x + 0.5, y0 + 0.5, w - 1, bodyH - 1);

      const emphasize = on || guide || wrong || hit;
      const showLabel = this.computerKeyLabels
        ? this.computerKeyLabels.has(midi) || (guide && this.lessonFingers?.has(midi))
        : this.notationMode === "finger"
          ? defaultCPositionFinger(midi) != null || this.lessonFingers?.has(midi)
          : this.notationMode !== "staff" || (guide && this.lessonFingers?.has(midi));

      if (showLabel) {
        const useFingerOnGuide =
          guide && this.lessonFingers?.has(midi) && !this.computerKeyLabels;
        ctx.fillStyle =
          this.computerKeyLabels && !(guide && this.lessonFingers?.has(midi))
            ? "#17130f"
            : style.label;
        const fontPx =
          guide && this.lessonFingers?.has(midi)
            ? Math.max(11, Math.min(16, w * 0.32))
            : this.computerKeyLabels
              ? Math.max(9, Math.min(14, w * 0.28))
              : this.notationMode === "letter"
                ? Math.max(8, Math.min(12, w * 0.22))
                : Math.max(9, w * 0.26);
        ctx.font = `${emphasize || useFingerOnGuide ? "700 " : ""}${fontPx}px "IBM Plex Sans", sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(this.label(midi), x + w / 2, y0 + bodyH - 12);
      }

      if (guide && !wrong && !hit) {
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.beginPath();
        ctx.arc(x + w / 2, y0 + bodyH * 0.3, w * 0.26, 0, Math.PI * 2);
        ctx.fill();
      } else if (on && !guide && !wrong) {
        ctx.fillStyle = "rgba(47,93,80,0.25)";
        ctx.beginPath();
        ctx.arc(x + w / 2, y0 + bodyH * 0.32, w * 0.22, 0, Math.PI * 2);
        ctx.fill();
      }
    });

    const bw = w * 0.58;
    const bh = h * 0.58;
    for (const midi of this.blackKeys) {
      const wi = this.whiteIndexForMidi(midi);
      if (wi < 0) continue;
      const x = (wi + 1) * w - bw / 2;
      const on = this.active.has(midi);
      const guide = this.guideNotes.has(midi);
      const wrong = this.wrongNotes.has(midi);
      const hit = this.hitNotes.has(midi);
      const pressed = on || hit;
      const y0 = pressed ? 4 : 0;
      const keyH = pressed ? bh * 0.96 : bh;

      // Drop shadow under black key
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fillRect(x + 2, y0 + 4, bw, keyH);

      const bg = ctx.createLinearGradient(x, y0, x + bw, y0 + keyH);
      if (wrong) {
        bg.addColorStop(0, "#ef9a9a");
        bg.addColorStop(1, WRONG_BLACK);
      } else if (hit) {
        bg.addColorStop(0, "#a5d6a7");
        bg.addColorStop(1, HIT_BLACK);
      } else if (guide) {
        bg.addColorStop(0, "#66bb6a");
        bg.addColorStop(1, GUIDE_BLACK);
      } else if (on) {
        bg.addColorStop(0, "#5a8f7e");
        bg.addColorStop(1, "#2a4a40");
      } else {
        bg.addColorStop(0, "#3a342e");
        bg.addColorStop(0.4, "#1a1612");
        bg.addColorStop(1, "#0c0a08");
      }
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.moveTo(x + 1, y0);
      ctx.lineTo(x + bw - 1, y0);
      ctx.lineTo(x + bw - 2, y0 + keyH - 3);
      ctx.quadraticCurveTo(x + bw / 2, y0 + keyH + 1, x + 2, y0 + keyH - 3);
      ctx.closePath();
      ctx.fill();

      // Gloss on black key
      ctx.fillStyle = "rgba(255,255,255,0.12)";
      ctx.fillRect(x + 3, y0 + 2, bw - 6, keyH * 0.22);

      const showComputerLabel = this.computerKeyLabels?.has(midi) ?? false;
      const showLessonFinger = guide && (this.lessonFingers?.has(midi) ?? false);
      const showNotationLabel =
        !this.computerKeyLabels &&
        this.notationMode !== "letter" &&
        this.notationMode !== "staff" &&
        (guide || wrong || hit || on);
      if (showComputerLabel || showNotationLabel || showLessonFinger) {
        ctx.fillStyle = "#fff";
        ctx.font = `700 ${Math.max(8, Math.min(13, bw * 0.34))}px "IBM Plex Sans", sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(this.label(midi), x + bw / 2, y0 + keyH - 8);
      }
    }
  }

  private hitTest(clientX: number, clientY: number): number | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cssW = rect.width;
    const cssH = rect.height;
    const { w } = this.whiteGeometry(cssW, cssH);
    const bw = w * 0.58;
    const bh = cssH * 0.58;

    if (y <= bh) {
      for (const midi of this.blackKeys) {
        const wi = this.whiteIndexForMidi(midi);
        const bx = (wi + 1) * w - bw / 2;
        if (x >= bx && x <= bx + bw) return midi;
      }
    }

    const idx = Math.floor(x / w);
    if (idx < 0 || idx >= this.whiteKeys.length) return null;
    return this.midiAtWhiteIndex(idx);
  }

  private bindPointer(): void {
    // Track each touch separately. A single downMidi drops/reorders notes when
    // the player taps rapidly or uses more than one finger on Android.
    const downByPointer = new Map<number, number>();

    const release = (e: PointerEvent) => {
      const midi = downByPointer.get(e.pointerId);
      if (midi != null && this.onPointerNote) {
        this.onPointerNote("noteoff", midi);
      }
      downByPointer.delete(e.pointerId);
    };

    this.canvas.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      this.canvas.setPointerCapture(e.pointerId);
      const midi = this.hitTest(e.clientX, e.clientY);
      if (midi == null) return;
      downByPointer.set(e.pointerId, midi);
      this.onPointerNote?.("noteon", midi);
    });

    this.canvas.addEventListener("pointerup", release);
    this.canvas.addEventListener("pointercancel", release);
  }
}
